import { useTranslation } from 'react-i18next';

import {
  type DatabaseTable,
  HISTORY_CATEGORIES,
  type HistoryCategory,
  type HistoryFilters,
  NO_HISTORY_FILTERS,
} from '@/entities/database';
import { Button, Combobox, Icons, IconButton, Popover } from '@/shared/ui';

import styles from './History.module.css';

export interface Person {
  id: string;
  name: string;
}

const PERIODS = [
  { id: 'any', hours: null },
  { id: 'day', hours: 24 },
  { id: 'week', hours: 24 * 7 },
  { id: 'month', hours: 24 * 30 },
] as const;
type Period = (typeof PERIODS)[number]['id'];

const CATEGORY_LABELS = {
  records: 'databases.history.categories.records',
  structure: 'databases.history.categories.structure',
  views: 'databases.history.categories.views',
  database: 'databases.history.categories.database',
  access: 'databases.history.categories.access',
  export: 'databases.history.categories.export',
} as const;

interface Props {
  filters: HistoryFilters;
  onChange: (filters: HistoryFilters) => void;
  /** What the viewer may read at all (a reader without content access has only some). */
  categories: readonly HistoryCategory[];
  tables: readonly DatabaseTable[];
  people: readonly Person[];
}

/** Compact filters: category chips in the open, the rest (person, table, period) in a popover. */
export function HistoryFiltersBar({ filters, onChange, categories, tables, people }: Props) {
  const { t } = useTranslation();
  const active = filters.categories;
  const allowed = HISTORY_CATEGORIES.filter((category) => categories.includes(category));
  const more = [filters.actorId, filters.tableId, filters.since].filter(Boolean).length;
  const period: Period =
    PERIODS.find(
      (p) => p.hours !== null && filters.since !== null && matches(filters.since, p.hours),
    )?.id ?? 'any';
  const set = (patch: Partial<HistoryFilters>) => {
    onChange({ ...filters, ...patch });
  };
  const toggle = (category: HistoryCategory) => {
    set({
      categories: active.includes(category)
        ? active.filter((c) => c !== category)
        : [...active, category],
    });
  };
  return (
    <div className={styles.filters} data-testid="history-filters">
      <div className={styles.chips} role="group" aria-label={t('databases.history.filters')}>
        <button
          type="button"
          className={`${styles.chip} ${active.length === 0 ? styles.chipOn : ''}`}
          aria-pressed={active.length === 0}
          onClick={() => {
            set({ categories: [] });
          }}
        >
          {t('databases.history.categoryAll')}
        </button>
        {allowed.map((category) => (
          <button
            key={category}
            type="button"
            className={`${styles.chip} ${active.includes(category) ? styles.chipOn : ''}`}
            aria-pressed={active.includes(category)}
            onClick={() => {
              toggle(category);
            }}
          >
            {t(CATEGORY_LABELS[category])}
          </button>
        ))}
      </div>
      <Popover
        label={t('databases.history.filters')}
        width={280}
        alignEnd
        trigger={(props) => (
          <IconButton
            {...props}
            label={`${t('databases.history.filters')}${more > 0 ? ` (${String(more)})` : ''}`}
            icon={<Icons.sliders />}
          />
        )}
      >
        {() => (
          <div className={styles.popover}>
            <div className={styles.field}>
              <span>{t('databases.history.actor')}</span>
              <Combobox
                label={t('databases.history.actor')}
                value={filters.actorId ?? ''}
                options={[
                  { value: '', label: t('databases.history.anyActor') },
                  ...people.map((person) => ({ value: person.id, label: person.name })),
                ]}
                onChange={(next) => {
                  set({ actorId: next || null });
                }}
              />
            </div>
            {tables.length > 0 && (
              <div className={styles.field}>
                <span>{t('databases.history.table')}</span>
                <Combobox
                  label={t('databases.history.table')}
                  value={filters.tableId ?? ''}
                  options={[
                    { value: '', label: t('databases.history.anyTable') },
                    ...tables.map((table) => ({ value: table.id, label: table.name })),
                  ]}
                  onChange={(next) => {
                    set({ tableId: next || null });
                  }}
                />
              </div>
            )}
            <label className={styles.field}>
              <span>{t('databases.history.period')}</span>
              <select
                value={period}
                onChange={(event) => {
                  const hours = PERIODS.find((p) => p.id === event.target.value)?.hours ?? null;
                  set({
                    since:
                      hours === null
                        ? null
                        : new Date(Date.now() - hours * 3_600_000).toISOString(),
                  });
                }}
              >
                {PERIODS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {t(`databases.history.periods.${p.id}`)}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="button"
              variant="ghost"
              disabled={more === 0 && active.length === 0}
              onClick={() => {
                onChange(NO_HISTORY_FILTERS);
              }}
            >
              {t('databases.history.reset')}
            </Button>
          </div>
        )}
      </Popover>
    </div>
  );
}

/** `since` was chosen as "now minus `hours`" (to the minute, whenever the choice was made). */
function matches(since: string, hours: number): boolean {
  const expected = Date.now() - hours * 3_600_000;
  return Math.abs(new Date(since).getTime() - expected) < 3_600_000 * (hours > 24 ? 24 : 1);
}
